/**
 * Ensures a URL has an https:// protocol prefix.
 * A URL that already carries a scheme — `http(s)://`, another `scheme://`, or a schemeless-authority
 * scheme like `mailto:`/`tel:`/`sms:` — is returned unchanged. A scheme-less value (bare host, with
 * or without a port) gets `https://` prepended.
 *
 * @param url - The URL string to normalize
 * @returns The URL with https:// protocol
 *
 * @example
 * ```typescript
 * ensureHttps("example.com");           // "https://example.com"
 * ensureHttps("www.example.com");       // "https://www.example.com"
 * ensureHttps("example.com:8080");      // "https://example.com:8080"
 * ensureHttps("https://example.com");   // "https://example.com"
 * ensureHttps("http://example.com");    // "http://example.com"
 * ensureHttps("mailto:a@b.com");        // "mailto:a@b.com"
 * ensureHttps("tel:+15551234567");      // "tel:+15551234567"
 * ensureHttps("");                      // ""
 * ```
 */
export const toYouTubeEmbedUrl = (url: string): string | null => {
  if (!url || !url.trim()) return null;

  let parsed: URL;
  try {
    parsed = new URL(ensureHttps(url));
  } catch {
    return null;
  }

  const hostname = parsed.hostname.replace(/^www\./, "");

  let videoId: string | null = null;

  if (hostname === "youtube.com") {
    if (parsed.pathname.startsWith("/embed/")) {
      videoId = parsed.pathname.slice("/embed/".length);
    } else {
      videoId = parsed.searchParams.get("v");
    }
  } else if (hostname === "youtu.be") {
    videoId = parsed.pathname.slice(1);
  }

  if (!videoId) return null;

  return `https://www.youtube.com/embed/${videoId}`;
};

// A leading scheme we should leave alone: either an authority-based scheme (`http://`, `ftp://`, …)
// or one of the schemeless-authority schemes (`mailto:`, `tel:`, `sms:`). A bare `host:port`
// (e.g. `example.com:8080`) is deliberately NOT matched, so it still gets `https://` prepended.
const HAS_SCHEME = /^([a-z][a-z\d+.-]*:\/\/|(?:mailto|tel|sms):)/i;

export function ensureHttps(url: string): string {
  if (!url) return url;

  const trimmed = url.trim();
  if (!trimmed) return trimmed;

  // Already carries a scheme (http(s)://, ftp://, mailto:, tel:, …)
  if (HAS_SCHEME.test(trimmed)) {
    return trimmed;
  }

  // Prepend https://
  return `https://${trimmed}`;
}

// Local dev portal, served alongside a locally-running API. Port is fixed even though the API port
// (and the app's own dev port) can vary, so we can't derive it from the API host. Callers whose local
// portal listens elsewhere (apps/web's dev server reaches portal-web on 4001) pass `localOrigin`.
export const DEFAULT_LOCAL_PORTAL_ORIGIN = "http://localhost:3030";

/**
 * Derives the portal origin from the API host currently in use. The portal environment always mirrors
 * the API the app is talking to, so a dev pointed at the staging/production API links to the matching
 * portal, and any local API (whatever the port) links to the local portal (which ignores the subdomain).
 * Anything unrecognized — including a malformed `apiHost` — falls back to the production portal.
 *
 * @param apiHost - The API URL the app is currently talking to
 * @param subdomain - The organization's portal subdomain
 * @param localOrigin - Origin to use when `apiHost` is local; defaults to `http://localhost:3030`
 *
 * @example
 * ```typescript
 * resolvePortalOrigin("https://api.tellescope.com", "acme");          // "https://acme.portal.tellescope.com"
 * resolvePortalOrigin("https://staging-api.tellescope.com", "acme");  // "https://acme.staging-portal.tellescope.com"
 * resolvePortalOrigin("http://localhost:8080", "acme");               // "http://localhost:3030"
 * resolvePortalOrigin("http://localhost:8080", "acme", "http://localhost:4001"); // "http://localhost:4001"
 * ```
 */
export const resolvePortalOrigin = (
  apiHost: string,
  subdomain: string,
  localOrigin: string = DEFAULT_LOCAL_PORTAL_ORIGIN
): string => {
  let hostname: string;
  try {
    hostname = new URL(apiHost).hostname;
  } catch {
    return `https://${subdomain}.portal.tellescope.com`;
  }

  if (hostname === "localhost" || hostname === "127.0.0.1") return localOrigin;

  const portalSubdomain = hostname.includes("staging") ? "staging-portal" : "portal";
  return `https://${subdomain}.${portalSubdomain}.tellescope.com`;
};

/**
 * Builds the public URL for an appointment booking page.
 *
 * The portal environment mirrors `apiHost` (the API URL the app is currently talking to), so this
 * works in local development regardless of whether the dev session is pointed at the local, staging,
 * or production API:
 * - any localhost API (any port) → `http://localhost:3030` (subdomain ignored)
 * - a `staging` API host → `https://{subdomain}.staging-portal.tellescope.com`
 * - any other host → `https://{subdomain}.portal.tellescope.com` (production)
 *
 * Returns `null` when `subdomain` or `businessId` is missing — callers should treat that as
 * "link not available yet" rather than emitting a broken link with empty query params.
 *
 * @example
 * ```typescript
 * buildBookingPageURL({ subdomain: "acme", businessId: "b1", pageId: "p1", apiHost: "https://api.tellescope.com" });
 * // "https://acme.portal.tellescope.com/appointment-booking-page?businessId=b1&appointmentBookingPageId=p1"
 * ```
 */
export const buildBookingPageURL = ({
  subdomain,
  businessId,
  pageId,
  apiHost,
}: {
  subdomain: string | undefined;
  businessId: string | undefined;
  pageId: string;
  apiHost: string;
}): string | null => {
  if (!subdomain || !businessId) return null;

  const origin = resolvePortalOrigin(apiHost, subdomain);
  const params = new URLSearchParams({ businessId, appointmentBookingPageId: pageId });
  return `${origin}/appointment-booking-page?${params.toString()}`;
};
